// CAMINHO: src/components/cases/AddMovementModal.tsx
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from 'lucide-react';
import { Movimentacao, ProcessData } from '@/types/datajud';

interface AddMovementModalProps {
  caseId: string;
  currentDatajudJson: ProcessData; // Passa o objeto datajud_json atual
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess: (updatedDatajudJson: ProcessData) => void; // Retorna o objeto atualizado
}

export const AddMovementModal = ({ caseId, currentDatajudJson, isOpen, onOpenChange, onSuccess }: AddMovementModalProps) => {
  const [nome, setNome] = useState('');
  const [complemento, setComplemento] = useState('');
  const [dataHora, setDataHora] = useState(new Date().toISOString().slice(0, 16)); // Formato YYYY-MM-DDTHH:mm
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome || !dataHora) {
      toast({ title: "Campos obrigatórios", description: "Por favor, preencha o nome e a data/hora do andamento.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const newMovement: Movimentacao = {
        nome,
        complemento,
        dataHora: new Date(dataHora).toISOString(),
      };

      const baseDatajud: any = currentDatajudJson || {};
      // Compatibiliza chaves possíveis (movimentacoes vs movimentos)
      const existingMovs = baseDatajud.movimentacoes || baseDatajud.movimentos || [];
      const updatedMovimentacoes = [newMovement, ...existingMovs];
      const updatedDatajudJson = { ...baseDatajud, movimentacoes: updatedMovimentacoes } as ProcessData;

      // A atualização no Supabase será feita no componente pai (CaseDetailPage)
      // que tem acesso direto à função de atualização do caseDetails
      onSuccess(updatedDatajudJson);

      toast({ title: "Sucesso!", description: "Novo andamento adicionado." });
      onOpenChange(false); // Fecha o modal
      // Limpa o formulário
      setNome('');
      setComplemento('');
      setDataHora(new Date().toISOString().slice(0, 16));

    } catch (err: any) {
      toast({ title: "Erro ao adicionar andamento", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Novo Andamento</DialogTitle>
          <DialogDescription>
            Preencha os detalhes para registrar um novo andamento manual para este processo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="nome" className="text-right">Nome</Label>
              <Input id="nome" value={nome} onChange={(e) => setNome(e.target.value)} className="col-span-3" placeholder="Ex: Despacho" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dataHora" className="text-right">Data/Hora</Label>
              <Input id="dataHora" type="datetime-local" value={dataHora} onChange={(e) => setDataHora(e.target.value)} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="complemento" className="text-right">Complemento</Label>
              <Textarea id="complemento" value={complemento} onChange={(e) => setComplemento(e.target.value)} className="col-span-3" placeholder="Detalhes adicionais (opcional)" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} 
              Adicionar Andamento
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
